import React, { useState, useContext, useEffect } from 'react';
import { api } from '../api';
import { AuthContext } from './AuthGate';

// Minimal fallback used only until /api/deal-stages responds. The real list
// (labels, colors, is_lost/is_won flags) is loaded from the backend — same
// source DealsPanel uses — so this modal can't drift from configured stages.
const FALLBACK_STAGES = [
  { id: 'prospecting', label: 'Prospecting', is_lost: false, is_won: false, in_funnel: true },
];

/**
 * Modal launched from CompanyDetailPanel. Creates a deal scoped to the company.
 *
 * Props:
 *   company   — required, must have .id, .name, .contacts (array)
 *   onClose   — () => void
 *   onCreated — (deal) => void
 */
export default function CreateDealModal({ company, onClose, onCreated }) {
  const auth = useContext(AuthContext);
  const me = auth?.user;

  const [name, setName] = useState(`${company.name} — `);
  const [stages, setStages] = useState(FALLBACK_STAGES);
  const [lostReasons, setLostReasons] = useState([]);
  const [stage, setStage] = useState('');
  const [lossReasonId, setLossReasonId] = useState('');
  const [amount, setAmount] = useState('');
  const [closeDate, setCloseDate] = useState('');
  const [primaryContactId, setPrimaryContactId] = useState(
    company.contacts?.[0]?.id || ''
  );
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Load configurable stages + lost reasons from the backend (same endpoints
  // DealsPanel uses). Default the stage to the first in-funnel, non-closed
  // stage so we never create directly into closed_won/closed_lost.
  useEffect(() => {
    let cancelled = false;
    api.listDealStages()
      .then((rows) => {
        if (cancelled || !rows || !rows.length) return;
        setStages(rows);
        const first = rows.find(s => s.in_funnel && !s.is_won && !s.is_lost) || rows[0];
        if (first) setStage(first.id);
      })
      .catch(() => {});
    api.listLostReasons().then(rs => { if (!cancelled) setLostReasons(rs || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Default stage before the API responds.
  useEffect(() => {
    if (!stage && stages.length) {
      const first = stages.find(s => s.in_funnel && !s.is_won && !s.is_lost) || stages[0];
      if (first) setStage(first.id);
    }
  }, [stages, stage]);

  const selectedStage = stages.find(s => s.id === stage);
  const isLostStage = !!selectedStage?.is_lost;

  async function submit(e) {
    e?.preventDefault?.();
    if (!name.trim()) {
      setError('Name is required'); return;
    }
    // Require a loss reason when creating directly into a closed-lost stage —
    // mirrors the requirement enforced in DealsPanel's save/stage-move.
    if (isLostStage && !lossReasonId) {
      setError('Pick a closed-lost reason before creating this deal.');
      return;
    }
    setBusy(true); setError('');
    try {
      const body = {
        name: name.trim(),
        company_id: company.id,
        stage,
        owner: me?.email || me?.name || 'unknown',
      };
      if (isLostStage && lossReasonId) body.loss_reason_id = lossReasonId;
      if (amount)             body.amount = parseFloat(amount);
      if (closeDate)          body.close_date = closeDate;
      if (primaryContactId)   body.primary_contact_id = primaryContactId;
      if (notes.trim())       body.notes = notes.trim();
      const result = await api.createDeal(body);
      onCreated?.({ ...body, id: result.id });
    } catch (e) {
      setError(e.message || 'Failed to create deal');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">New deal</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
        </div>
        <div className="text-xs text-slate-500 mb-4">Company: <span className="text-slate-300">{company.name}</span></div>

        <label className="block mb-3">
          <span className="text-xs text-slate-400">Deal name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            className="w-full mt-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
          />
        </label>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="block">
            <span className="text-xs text-slate-400">Stage</span>
            <select
              value={stage}
              onChange={(e) => setStage(e.target.value)}
              className="w-full mt-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
            >
              {stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-400">Amount (USD)</span>
            <input
              type="number" min="0" step="100"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="optional"
              className="w-full mt-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
            />
          </label>
        </div>

        {isLostStage && (
          <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-3 mb-3">
            <label className="block">
              <span className="text-xs font-medium text-red-300">Closed-lost reason *</span>
              <select
                value={lossReasonId}
                onChange={(e) => setLossReasonId(e.target.value)}
                className="w-full mt-1 bg-slate-800 border border-red-700/50 rounded px-3 py-2 text-sm"
              >
                <option value="">— pick a reason —</option>
                {lostReasons.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </label>
            <div className="text-[11px] text-red-300/70 mt-1">
              To keep the deal alive, pick an active stage instead of closing it as lost.
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="block">
            <span className="text-xs text-slate-400">Primary contact</span>
            <select
              value={primaryContactId}
              onChange={(e) => setPrimaryContactId(e.target.value)}
              className="w-full mt-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
            >
              <option value="">— none —</option>
              {(company.contacts || []).map((c) => (
                <option key={c.id} value={c.id}>{c.full_name || c.first_name || c.email}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-400">Close date</span>
            <input
              type="date"
              value={closeDate}
              onChange={(e) => setCloseDate(e.target.value)}
              className="w-full mt-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
            />
          </label>
        </div>

        <label className="block mb-4">
          <span className="text-xs text-slate-400">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full mt-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
            placeholder="optional"
          />
        </label>

        {error && <div className="text-red-400 text-xs mb-3">{error}</div>}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-slate-400 hover:text-white"
          >Cancel</button>
          <button
            type="submit" disabled={busy}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 rounded text-sm font-semibold text-white"
          >
            {busy ? 'Creating…' : 'Create deal'}
          </button>
        </div>
      </form>
    </div>
  );
}
